<!-- ============================================================
  SlipVerificationPanel.vue (<slip-verification-panel>) — shows the
  bank-verification verdict (SlipOK) for an uploaded payment slip:
  genuine / duplicate / amount mismatch / wrong receiver / unverified.
  Shared by CustomerCourse.vue and PrivateBookingEditDialog.vue's slip
  viewer dialogs so the status→icon/label mapping and markup live once.

  Props: verification (Object|null — a row from GET /slipVerification/*), loading (Boolean),
         context ('course'|'private') + refer — when provided and the status is
         'skipped' (couldn't verify: bank outage / quota / timeout), a "re-check"
         button appears that POSTs /reverifySlip and emits 'reverified' with the
         fresh row so the parent can swap its verification object.
============================================================ -->
<template>
  <div v-if="loading" class="slip-verify sv-muted">
    <span class="mdi mdi-timer-sand"></span>
    <div class="sv-txt"><b>{{ $t('customerCourse.slipVerifyChecking') }}</b></div>
  </div>
  <div v-else-if="verification && meta" class="slip-verify" :class="meta.cls">
    <span class="mdi" :class="meta.icon"></span>
    <div class="sv-txt">
      <b>{{ meta.label }}</b>
      <div v-if="verification.verify_status === 'verified'" class="sv-detail">
        {{ $t('customerCourse.slipAmount') }} {{ formatMoney(verification.amount) }} ฿<template v-if="verification.trans_datetime"> · {{ $t('customerCourse.slipWhen') }} {{ $formatDateTime(verification.trans_datetime) }}</template><template v-if="verification.sender_name"> · {{ $t('customerCourse.slipFrom') }} {{ verification.sender_name }}</template>
      </div>
      <div v-else-if="verification.reason" class="sv-detail">{{ verification.reason }}</div>
      <div v-if="reverifyError" class="sv-detail">{{ reverifyError }}</div>
    </div>
    <button v-if="canReverify" type="button" class="sv-retry" :disabled="reverifying" @click="reverify">
      <span class="mdi" :class="reverifying ? 'mdi-loading mdi-spin' : 'mdi-refresh'"></span>
      {{ reverifying ? $t('customerCourse.slipReverifying') : $t('customerCourse.slipReverify') }}
    </button>
  </div>
  <div v-else class="slip-verify sv-muted">
    <span class="mdi mdi-shield-off-outline"></span>
    <div class="sv-txt">
      <b>{{ $t('customerCourse.slipVerifyNone') }}</b>
      <div v-if="reverifyError" class="sv-detail">{{ reverifyError }}</div>
    </div>
    <!-- multi-slip: ใบที่ยังไม่เคยถูกตรวจเลย (แนบตอน SlipOK ล่ม/ยังไม่ตั้งค่า) ต้องสั่งตรวจครั้งแรกได้ -->
    <button v-if="canReverify" type="button" class="sv-retry" :disabled="reverifying" @click="reverify">
      <span class="mdi" :class="reverifying ? 'mdi-loading mdi-spin' : 'mdi-refresh'"></span>
      {{ reverifying ? $t('customerCourse.slipReverifying') : $t('customerCourse.slipReverify') }}
    </button>
  </div>
</template>

<script>
import axios from 'axios';
import { formatMoney } from '@/utils/money';

export default {
  name: 'SlipVerificationPanel',
  props: {
    verification: { type: Object, default: null },
    loading: { type: Boolean, default: false },
    // สำหรับปุ่มตรวจใหม่ — ไม่ส่งมา = ปุ่มไม่โชว์ (panel เป็นแบบอ่านอย่างเดียวเหมือนเดิม)
    context: { type: String, default: null },
    refer: { type: [String, Number], default: null },
    // multi-slip: ตรวจใหม่ "ใบนี้" ไม่ใช่ใบล่าสุดของรายการ (ไม่ส่ง = พฤติกรรมเดิม)
    slipid: { type: [String, Number], default: null },
    // วิธีชำระของรายการ — เงินสด/บัตรเครดิตไม่มีสลิปโอนให้ตรวจ ปุ่มต้องไม่โชว์ (backend กันซ้ำอีกชั้น)
    paymentMethod: { type: String, default: null },
  },
  emits: ['reverified'],
  data: () => ({
    reverifying: false,
    reverifyError: null,
  }),
  computed: {
    // ไอคอน/สี/ข้อความ ตามสถานะผลตรวจสลิป — โทนสีใช้ design token เดิม (success/error/warning)
    meta() {
      const v = this.verification;
      if (!v) return null;
      const map = {
        verified:        { icon: 'mdi-check-decagram',     cls: 'sv-ok',    label: this.$t('customerCourse.slipVerified') },
        duplicate:       { icon: 'mdi-alert-octagon',      cls: 'sv-bad',   label: this.$t('customerCourse.slipDuplicate') },
        wrong_receiver:  { icon: 'mdi-alert-octagon',      cls: 'sv-bad',   label: this.$t('customerCourse.slipWrongReceiver') },
        amount_mismatch: { icon: 'mdi-alert',              cls: 'sv-warn',  label: this.$t('customerCourse.slipAmountMismatch') },
        failed:          { icon: 'mdi-alert',              cls: 'sv-warn',  label: this.$t('customerCourse.slipFailed') },
        skipped:         { icon: 'mdi-shield-off-outline', cls: 'sv-muted', label: this.$t('customerCourse.slipSkipped') },
      };
      return map[v.verify_status] || map.skipped;
    },
    // ตรวจใหม่ได้เฉพาะ skipped (ตรวจไม่ได้ชั่วคราว) + วิธีชำระเป็นเงินโอน/ไม่ระบุเท่านั้น
    // — backend บังคับกฎเดียวกันอีกชั้น
    canReverify() {
      const v = this.verification;
      if (this.paymentMethod && this.paymentMethod !== 'transfer') return false;
      if (!this.context || this.refer === null || this.refer === '') return false;
      // multi-slip: รู้ว่าเป็นใบไหน (slipid) → ใบที่ยังไม่มีผลตรวจเลยก็สั่งตรวจครั้งแรกได้
      if (!v) return !!this.slipid;
      return !!(v.verify_status === 'skipped' && (v.slip_image_url || this.slipid));
    },
  },
  watch: {
    verification() { this.reverifyError = null; },
  },
  methods: {
    // ยอดเงินต้องแสดง 2 ตำแหน่งเสมอเหมือนทุกหน้าในระบบ (เจ้าของสั่ง 2026-08-05)
    // เดิมตรงนี้พิมพ์ค่าดิบจาก API ตรง ๆ — เป็นจุดเดียวในระบบที่ไม่ผ่านตัวจัดรูปแบบเลย
    // ทำให้ยอดสลิปไม่มีตัวคั่นหลักพันและทศนิยมไม่แน่นอน (1500 / 1500.00 แล้วแต่ชนิดที่ API ส่งมา)
    formatMoney,
    async reverify() {
      this.reverifying = true;
      this.reverifyError = null;
      try {
        const response = await axios.post(
          this.baseURL + '/reverifySlip',
          { context: this.context, refer: this.refer, slipid: this.slipid },
          { headers: { Authorization: 'Bearer ' + this.$store.getters.getToken } }
        );
        if (response.data && response.data.success && response.data.verification) {
          this.$emit('reverified', response.data.verification);
        } else {
          this.reverifyError = (response.data && response.data.message) || this.$t('customerCourse.slipReverifyFail');
        }
      } catch (error) {
        this.reverifyError = this.$t('customerCourse.slipReverifyFail');
      } finally {
        this.reverifying = false;
      }
    },
  },
};
</script>
